import type { PreloadLink } from "@/ssr/types";

/**
 * Homepage hero photo: the one place that knows its variants, shared by the
 * <picture> in Hero.tsx and the <link rel="preload"> the homepage emits
 * (`preload` in src/routes/index.tsx), so the preloaded URL is always the one
 * the <picture> picks. Files are generated by scripts/generate-hero-images.ts
 * and copied to /assets/ by build.ts (ASSET_FILES).
 */

export const HERO_SOURCE_WIDTH = 1600;
export const HERO_SOURCE_HEIGHT = 900;
export const HERO_PORTRAIT_WIDTH = 800;

/**
 * Horizontal focal point (object-position). Pushed right: on a wide screen
 * this barely moves the frame, but a phone crops ~70% of the width away and a
 * centred focal point would cut the subject out of the photo entirely. The
 * portrait crop is cut around this same point, so changing it means
 * regenerating the images.
 */
export const HERO_FOCAL_X = 0.72;
export const HERO_OBJECT_POSITION = `${HERO_FOCAL_X * 100}% center`;

/**
 * Viewports at most this wide relative to their height use the 800x900
 * portrait crop. The hero is at least one viewport tall, so its own aspect is
 * never wider than the viewport's, and 4/5 < 800/900 leaves a margin: the crop
 * always covers the hero and shows the same pixels as the full frame would.
 */
const PORTRAIT_MEDIA = "(max-aspect-ratio: 4/5)";
const LANDSCAPE_MEDIA = `not all and ${PORTRAIT_MEDIA}`;

/** object-fit: cover on a 16:9 frame at least one viewport tall. */
const LANDSCAPE_SIZES = "max(100vw, 177.78vh)";

const landscapeSrcSet = (ext: "avif" | "webp") =>
  `/assets/hero-bg-1200.${ext} 1200w, /assets/hero-bg-1600.${ext} 1600w`;

export interface HeroImageSource {
  type: string;
  media?: string;
  srcSet: string;
  sizes?: string;
}

/** In <picture> order: first match wins. */
export const HERO_SOURCES: readonly HeroImageSource[] = [
  { type: "image/avif", media: PORTRAIT_MEDIA, srcSet: "/assets/hero-bg-portrait.avif" },
  { type: "image/webp", media: PORTRAIT_MEDIA, srcSet: "/assets/hero-bg-portrait.webp" },
  { type: "image/jpeg", media: PORTRAIT_MEDIA, srcSet: "/assets/hero-bg-portrait.jpg" },
  { type: "image/avif", srcSet: landscapeSrcSet("avif"), sizes: LANDSCAPE_SIZES },
  { type: "image/webp", srcSet: landscapeSrcSet("webp"), sizes: LANDSCAPE_SIZES },
];

/** <img> fallback for browsers without avif/webp. */
export const HERO_FALLBACK_SRC = "/assets/hero-bg.jpg";

/**
 * Preloads for the avif variants (every current browser decodes avif; one
 * that does not skips a preload whose `type` it does not support). Split by
 * the same media query as the <picture>, so a phone fetches only the crop.
 */
export const HERO_IMAGE_PRELOADS: readonly PreloadLink[] = [
  {
    as: "image",
    type: "image/avif",
    href: "/assets/hero-bg-portrait.avif",
    media: PORTRAIT_MEDIA,
    fetchPriority: "high",
  },
  {
    as: "image",
    type: "image/avif",
    imageSrcSet: landscapeSrcSet("avif"),
    imageSizes: LANDSCAPE_SIZES,
    media: LANDSCAPE_MEDIA,
    fetchPriority: "high",
  },
];
